// SCR-STAFF-203 — تغییر گروهی قیمت (US-CAT-026؛ cat.price.bulk_change): درصد یا مبلغ برای یک دسته یا همه خدمات یک فهرست، گرد کردن،
// تاریخ مؤثر امروز یا آینده؛ پیش‌نمایش قیمت فعلی، تازه و تفاوت با امکان مستثنا کردن ردیف (AC-CAT-026.1)؛ اعمال با رکورد قابل بازگشت تا
// پیش از تاریخ مؤثر (AC-CAT-026.2، BR-CAT-028).
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type BulkPreviewRow,
  type BulkPreviewView,
  type BulkPriceChangeView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  useDisplaySettings,
  zBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  buildTree,
  catKeys,
  todayIn,
  toBasisPoints,
  useCategories,
  usePriceLists,
  useProviders,
  type CategoryNode,
} from './common';

export const SCR_STAFF_203 = 'SCR-STAFF-203';

function bulkSchema(today: string) {
  return z
    .object({
      priceListId: z.string(),
      categoryId: z.string(),
      kind: z.enum(['Percent', 'Amount']),
      percent: z.string(),
      amount: z.number().int().optional(),
      direction: z.enum(['up', 'down']),
      rounding: z.string(),
      effectiveFrom: zBusinessDate(),
    })
    .superRefine((value, ctx) => {
      if (value.kind === 'Percent' && !(Number(value.percent) > 0 && Number(value.percent) <= 500)) {
        ctx.addIssue({ code: 'custom', path: ['percent'], message: 'errors:validation.invalid' });
      }
      if (value.kind === 'Amount' && !(Number(value.amount) > 0)) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: 'common:validation.required' });
      }
      if (value.effectiveFrom && value.effectiveFrom < today) {
        ctx.addIssue({
          code: 'custom',
          path: ['effectiveFrom'],
          message: 'errors:error.price_effective_date_past',
        });
      }
    });
}
type BulkForm = z.input<ReturnType<typeof bulkSchema>>;

const ROUNDING = ['1', '1000', '10000', '100000', '1000000'] as const;

function flatten(nodes: CategoryNode[], depth = 0): { id: string; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.category.id, label: `${'— '.repeat(depth)}${node.category.name}` },
    ...flatten(node.children, depth + 1),
  ]);
}

export function BulkPricePage() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.price.bulk_change');
  const { timeZone } = useDisplaySettings();
  const today = todayIn(timeZone);
  const lists = usePriceLists();
  const categories = useCategories();
  const providers = useProviders();
  const idempotency = useIdempotencyKey();
  const [preview, setPreview] = useState<BulkPreviewView | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const [applied, setApplied] = useState<BulkPriceChangeView | null>(null);
  const [reverting, setReverting] = useState<BulkPriceChangeView | null>(null);

  const history = useQuery({
    queryKey: catKeys.bulk(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/bulk-price-changes')),
  });

  const { control, handleSubmit, setError, getValues } = useForm<BulkForm>({
    resolver: zodResolver(bulkSchema(today)),
    defaultValues: {
      priceListId: '',
      categoryId: '',
      kind: 'Percent',
      percent: '',
      amount: undefined,
      direction: 'up',
      rounding: '100000',
      effectiveFrom: today,
    },
  });
  const kind = useWatch({ control, name: 'kind' });

  const request = (form: BulkForm) => {
    const value = form.kind === 'Percent' ? toBasisPoints(Number(form.percent)) : Number(form.amount);
    return {
      priceListId: form.priceListId || null,
      categoryId: form.categoryId || null,
      kind: form.kind,
      value: form.direction === 'down' ? -value : value,
      roundingUnit: Number(form.rounding),
      effectiveFrom: form.effectiveFrom,
      excludedServiceIds: [...excluded],
    };
  };

  const onError = (error: unknown) => {
    const count = applyServerErrors(error, setError, {
      fields: ['effectiveFrom'],
      codeFields: { 'PHX-CAT-0701': 'effectiveFrom', 'PHX-CAT-0702': 'effectiveFrom' },
    });
    setSubmitError(count > 0 ? undefined : error);
  };

  const previewMutation = useMutation({
    mutationFn: (form: BulkForm) =>
      unwrap(tenantApi.POST('/api/v1/catalog/bulk-price-changes:preview', { body: request(form) })),
    onMutate: () => {
      setSubmitError(undefined);
      setApplied(null);
    },
    onSuccess: (data) => {
      setPreview(data);
      setExcluded(new Set(data.rows.filter((row) => row.excluded).map((row) => row.serviceId)));
    },
    onError,
  });

  const apply = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/bulk-price-changes', {
          body: request(getValues()),
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (data) => {
      setConfirming(false);
      setApplied(data);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      setConfirming(false);
      onError(error);
    },
  });

  const revert = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/catalog/bulk-price-changes/{id}:revert', { params: { path: { id } } })),
    onSuccess: () => {
      setReverting(null);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
  });

  const toggle = (serviceId: string) =>
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });

  const previewColumns: DataTableColumn<BulkPreviewRow>[] = [
    {
      id: 'exclude',
      headerKey: 'cat:bulk.columns.include',
      cell: (row) => (
        <Checkbox
          checked={!excluded.has(row.serviceId)}
          onChange={() => toggle(row.serviceId)}
          disabled={!write.canWrite}
          slotProps={{
            input: {
              'aria-label': t('cat:bulk.include_named', { name: row.name }),
              'data-testid': `bulk-include-${row.code}`,
            } as object,
          }}
        />
      ),
    },
    { id: 'code', headerKey: 'cat:services.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'name',
      headerKey: 'cat:services.columns.name',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{row.name}</span>
          {row.providerId && (
            <Typography variant="caption" color="text.secondary">
              {providers.name(row.providerId)}
            </Typography>
          )}
        </Stack>
      ),
    },
    { id: 'method', headerKey: 'cat:prices.columns.method', cell: (row) => t(`cat:method.${row.method}`) },
    {
      id: 'current',
      headerKey: 'cat:bulk.columns.current',
      cell: (row) => <Money amountMinor={row.current.amount} currency={row.current.currency} />,
    },
    {
      id: 'proposed',
      headerKey: 'cat:bulk.columns.proposed',
      cell: (row) => (
        <span data-testid={`bulk-proposed-${row.code}`}>
          <Money amountMinor={row.proposed.amount} currency={row.proposed.currency} />
        </span>
      ),
    },
    {
      id: 'difference',
      headerKey: 'cat:bulk.columns.difference',
      cell: (row) => <Money amountMinor={row.difference.amount} currency={row.difference.currency} />,
    },
  ];

  const historyColumns: DataTableColumn<BulkPriceChangeView>[] = [
    {
      id: 'effectiveFrom',
      headerKey: 'cat:prices.columns.effective_from',
      cell: (row) => <JalaliDate value={row.effectiveFrom} />,
    },
    {
      id: 'change',
      headerKey: 'cat:bulk.columns.change',
      cell: (row) =>
        row.kind === 'Percent'
          ? t('cat:bulk.percent_value', { value: faDigits(Number(row.value) / 100) })
          : t('cat:bulk.amount_value', { value: faDigits(row.value) }),
    },
    { id: 'count', headerKey: 'cat:bulk.columns.count', cell: (row) => faDigits(row.entryCount) },
    {
      id: 'status',
      headerKey: 'cat:prices.columns.state',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Applied' ? 'active' : 'cancelled'}
          labelKey={`cat:bulk.status.${row.status}`}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'cat:prices.columns.actions',
      cell: (row) =>
        row.canRevert ? (
          <Button
            size="small"
            color="warning"
            disabled={!write.canWrite}
            onClick={() => setReverting(row)}
            data-testid={`bulk-revert-${row.id}`}
          >
            {t('cat:bulk.revert')}
          </Button>
        ) : null,
    },
  ];

  const applicable = preview
    ? preview.rows.filter((row) => !excluded.has(row.serviceId) && Number(row.difference.amount) !== 0).length
    : 0;
  const categoryOptions = flatten(buildTree(categories.data?.items ?? []));

  return (
    <ListPage
      screenId={SCR_STAFF_203}
      titleKey="cat:bulk.title"
      subtitleKey="cat:bulk.subtitle"
      filters={<WriteNotice write={write} />}
    >
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit((form) => previewMutation.mutate(form))();
            }}
          >
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <SelectRHF
                  control={control}
                  name="priceListId"
                  labelKey="cat:prices.list"
                  fullWidth
                  options={(lists.data?.items ?? []).map((list) => ({
                    value: list.type === 'Base' ? '' : list.id,
                    label: list.type === 'Base' ? t('cat:price_list.Base') : list.name,
                  }))}
                />
                <SelectRHF
                  control={control}
                  name="categoryId"
                  labelKey="cat:bulk.category"
                  fullWidth
                  options={[
                    { value: '', labelKey: 'cat:bulk.all_categories' },
                    ...categoryOptions.map((option) => ({ value: option.id, label: option.label })),
                  ]}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <SelectRHF
                  control={control}
                  name="kind"
                  labelKey="cat:bulk.kind"
                  options={(['Percent', 'Amount'] as const).map((value) => ({
                    value,
                    labelKey: `cat:bulk.kinds.${value}`,
                  }))}
                />
                {kind === 'Percent' ? (
                  <TextFieldRHF
                    control={control}
                    name="percent"
                    labelKey="cat:bulk.percent"
                    type="number"
                    required
                    slotProps={{
                      htmlInput: { min: 0.01, max: 500, step: 0.01, 'data-testid': 'bulk-percent' },
                    }}
                  />
                ) : (
                  <MoneyFieldRHF control={control} name="amount" labelKey="cat:bulk.amount" required />
                )}
                <SelectRHF
                  control={control}
                  name="direction"
                  labelKey="cat:bulk.direction"
                  options={[
                    { value: 'up', labelKey: 'cat:bulk.increase' },
                    { value: 'down', labelKey: 'cat:bulk.decrease' },
                  ]}
                />
                <SelectRHF
                  control={control}
                  name="rounding"
                  labelKey="cat:bulk.rounding"
                  options={ROUNDING.map((value) => ({
                    value,
                    label:
                      value === '1'
                        ? t('cat:bulk.no_rounding')
                        : t('cat:bulk.rounding_to', { value: faDigits(value) }),
                  }))}
                />
                <JalaliDatePickerRHF
                  control={control}
                  name="effectiveFrom"
                  labelKey="cat:prices.effective_from"
                  helperKey="cat:prices.effective_from_help"
                  required
                />
              </Stack>
            </Stack>
            {submitError ? <ErrorState error={submitError} compact /> : null}
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 2 }}
              disabled={!write.canWrite || previewMutation.isPending}
              data-testid="bulk-preview"
            >
              {t('cat:bulk.preview')}
            </Button>
          </Box>
        </Paper>

        {applied && (
          <Typography role="status" color="success.main" data-testid="bulk-applied">
            {t('cat:bulk.applied', { count: faDigits(applied.entryCount) })}
          </Typography>
        )}

        {preview && (
          <Stack spacing={1}>
            <DataTable
              captionKey="cat:bulk.preview_caption"
              columns={previewColumns}
              rows={preview.rows}
              getRowId={(row) => `${row.serviceId}-${row.providerId ?? ''}-${row.providerGroupId ?? ''}`}
              emptyTitleKey="cat:bulk.preview_empty"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" data-testid="bulk-applicable">
                {t('cat:bulk.applicable', { count: faDigits(applicable) })}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={!write.canWrite || applicable === 0 || apply.isPending}
                onClick={() => setConfirming(true)}
                data-testid="bulk-apply"
              >
                {t('cat:bulk.apply')}
              </Button>
            </Stack>
          </Stack>
        )}

        <DataTable
          captionKey="cat:bulk.history"
          columns={historyColumns}
          rows={history.data?.items}
          getRowId={(row) => row.id}
          isLoading={history.isPending}
          error={history.error}
          onRetry={() => void history.refetch()}
          emptyTitleKey="cat:bulk.history_empty"
        />
      </Stack>
      <ConfirmDialog
        open={confirming}
        titleKey="cat:bulk.confirm_title"
        messageKey="cat:bulk.confirm_message"
        messageValues={{ count: faDigits(applicable) }}
        confirmKey="cat:bulk.apply"
        busy={apply.isPending}
        error={apply.error}
        onConfirm={() => apply.mutate()}
        onCancel={() => setConfirming(false)}
      />
      <ConfirmDialog
        open={reverting !== null}
        titleKey="cat:bulk.revert_title"
        messageKey="cat:bulk.revert_message"
        confirmKey="cat:bulk.revert"
        destructive
        busy={revert.isPending}
        error={revert.error}
        onConfirm={() => reverting && revert.mutate(reverting.id)}
        onCancel={() => setReverting(null)}
      />
    </ListPage>
  );
}
